// hooks/useQrScanner.ts
import { useEffect, useRef } from "react";
import { Html5Qrcode, Html5QrcodeCameraScanConfig } from "html5-qrcode";

interface UseQrScannerOptions {
  elementId: string;
  onSuccess: (text: string) => void;
  onIdleTimeout?: () => void;
  beepOnSuccess?: boolean;
  idleTimeoutMs?: number;
  scanBoxSize?: number;
}

type CameraDevice = {
  id: string;
  label: string;
};

export const useQrScanner = ({
  elementId,
  onSuccess,
  onIdleTimeout,
  beepOnSuccess = true,
  idleTimeoutMs = 60000,
  scanBoxSize = 200,
}: UseQrScannerOptions) => {
  const qrCodeRef = useRef<Html5Qrcode | null>(null);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRunningRef = useRef<boolean>(false);
  const lastScanRef = useRef<{ text: string | null; time: number }>({ text: null, time: 0 });

  const playBeep = () => {
    const beep = new Audio("sound/qr-code-scan-beep.mp3");
    beep.play().catch(err => console.warn("🔇 Audio play failed:", err));
  };

  const resetIdleTimer = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      stopScanning();
      console.log("⏳ QR Scanner stopped due to idle timeout.");
      onIdleTimeout?.();
    }, idleTimeoutMs);
  };

  const stopScanning = () => {
    if (!qrCodeRef.current || !isRunningRef.current) return;

    qrCodeRef.current
      .stop()
      .then(() => {
        isRunningRef.current = false;
        return qrCodeRef.current?.clear();
      })
      .catch((err) => {
        if (err.message?.includes("scanner is not running")) {
          console.log("📷 Scanner already stopped.");
        } else {
          console.error("Error stopping QR scanner:", err);
        }
      });

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  };

  const startScanning = async (cameraId: string | { facingMode: string } = { facingMode: "environment" }) => {
    if (!qrCodeRef.current) {
      qrCodeRef.current = new Html5Qrcode(elementId);
    }

    if (isRunningRef.current) {
      console.log("⚠️ Scanner already running");
      return;
    }

    try {
      await qrCodeRef.current.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: scanBoxSize, height: scanBoxSize },
        },
        (decodedText) => {
          const now = Date.now();
          const { text: lastText, time: lastTime } = lastScanRef.current;

          // Prevent duplicate scan within 1s or same text as last
          if (decodedText === lastText && now - lastTime < 1000) {
            return;
          }

          lastScanRef.current = { text: decodedText, time: now };

          // console.log("✅ QR Scanned:", decodedText);
          
          resetIdleTimer();
          if (beepOnSuccess) playBeep();
          onSuccess(decodedText);
        },
        (errorMessage) => {
          // Optional: Handle scan failures
        }
      );
      isRunningRef.current = true;
      resetIdleTimer();
    } catch (err) {
      console.error("❌ Failed to start scanner:", err);
    }
  };

  const switchCamera = async (deviceId: string) => {
    stopScanning();
    setTimeout(() => {
      startScanning(deviceId);
    }, 300);
  };

  const getCameras = async (): Promise<CameraDevice[]> => {
    return Html5Qrcode.getCameras()
      .then((devices) => devices)
      .catch((err) => {
        console.error("Failed to get cameras:", err);
        return [];
      });
  };

  useEffect(() => {
    return () => {
      stopScanning(); // cleanup on unmount
    };
  }, []);

  return {
    startScanning,
    stopScanning,
    switchCamera,
    getCameras,
  };
};
